import axios from 'axios'
import { record as recordApiCall } from './apiHistory'

// Empty fallback → relative URLs → Vite proxy (dev) or pod nginx (prod) handles /api
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Attach JWT token to every request + stamp start time for the dev panel.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token_admin')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  config.metadata = { startTime: Date.now() }
  return config
})

// Normalize backend ErrorDescription → populate .message so all UI components
// that do err.response?.data?.message get a friendly, readable string even for
// 403/422 responses where the backend uses the richer ErrorDescription schema:
// { type, severity, correlationId, description, message }
function normalizeError(error) {
  const data = error.response?.data
  if (data && typeof data === 'object') {
    if (data.description && (!data.message || data.message === 'Access Denied')) {
      data.message = data.description
    }
  }
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    normalizeError(error)
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token_admin')
      // Redirect to login unless already there (avoid redirect loop)
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// ── Dev panel capture (last interceptor; sees the same payloads UI sees) ────
// Capture every settled request — success or failure — into the in-memory
// ring buffer that `ApiDevPanel` subscribes to. Runs AFTER normalizeError so
// the panel shows the same `.message` value that components consume.
api.interceptors.response.use(
  (response) => {
    const cfg = response.config || {}
    const startTime = cfg.metadata?.startTime
    recordApiCall({
      method: cfg.method,
      url: cfg.url,
      status: response.status,
      ok: true,
      duration: startTime ? Date.now() - startTime : 0,
      timestamp: new Date().toISOString(),
      requestHeaders: cfg.headers,
      requestBody: cfg.data,
      responseHeaders: response.headers,
      responseBody: response.data,
    })
    return response
  },
  (error) => {
    const cfg = error.config || {}
    const startTime = cfg.metadata?.startTime
    recordApiCall({
      method: cfg.method,
      url: cfg.url,
      status: error.response?.status || 0,
      ok: false,
      duration: startTime ? Date.now() - startTime : 0,
      timestamp: new Date().toISOString(),
      requestHeaders: cfg.headers,
      requestBody: cfg.data,
      responseHeaders: error.response?.headers,
      responseBody: error.response?.data ?? { message: error.message },
    })
    return Promise.reject(error)
  }
)

export default api
